import { useState } from 'react';
import { Paper, Title, Text, Stack, Button, Group, Alert, rem } from '@mantine/core';
import { Dropzone, MIME_TYPES } from '@mantine/dropzone';
import {
  IconUpload,
  IconFileTypeCsv,
  IconX,
  IconAlertCircle,
  IconFileImport,
} from '@tabler/icons-react';
import { useImportHourlyData, type ImportHourlyDataResult } from '../../hooks/useImportHourlyData';
import { importSummaryStore } from '../../../../stores/importSummaryStore';
import { TariffSelector } from './TariffSelector';
import { ProgressIndicator } from './ProgressIndicator';
import { ImportSummary } from './ImportSummary';
import { CsvPreview } from './CsvPreview';
import { previewCsvContent, type CsvPreviewResult } from '../../../../utils/csvPreview';

interface UploadCSVProps {
  onImportSuccess?: () => void;
}

export function UploadCSV({ onImportSuccess }: UploadCSVProps) {
  const [file, setFile] = useState<File | null>(null);
  const [tariff, setTariff] = useState<string | null>(null);
  const [result, setResult] = useState<ImportHourlyDataResult | null>(null);
  const [preview, setPreview] = useState<CsvPreviewResult | null>(null);
  const [csvContent, setCsvContent] = useState<string | null>(null);

  const { importHourlyData, loading, error, progress, reset } = useImportHourlyData();

  const handleDrop = async (files: File[]) => {
    const droppedFile = files[0];
    if (droppedFile) {
      setFile(droppedFile);
      setResult(null); // Clear previous result
      setPreview(null); // Clear previous preview
      reset(); // Clear previous error

      // Read file and generate preview
      try {
        const content = await droppedFile.text();
        setCsvContent(content);
        const previewResult = previewCsvContent(content);
        setPreview(previewResult);
      } catch (err) {
        reset();
        if (import.meta.env.DEV) {
          console.error('Preview error:', err);
        }
      }
    }
  };

  const handleReject = () => {
    // Dropzone will show rejection feedback automatically
  };

  const handleUpload = async () => {
    if (!csvContent || !tariff) return;

    try {
      const importResult = await importHourlyData({
        csvContent,
        tariffType: tariff as 'G11' | 'G12' | 'G12as' | 'G12w' | 'G13' | 'G13s',
      });

      setResult(importResult);

      // Invalidate all useGetImportSummary subscribers (cross-component cache bust).
      if (importResult.success) {
        importSummaryStore.invalidate();
        onImportSuccess?.();
      }
    } catch (err) {
      // Error is already set in the hook
    }
  };

  const handleCancelPreview = () => {
    setFile(null);
    setPreview(null);
    setCsvContent(null);
    reset();
  };

  const handleReset = () => {
    setFile(null);
    setResult(null);
    setPreview(null);
    setCsvContent(null);
    reset();
  };

  const canUpload = preview && preview.isValid && tariff && !loading;

  return (
    <Stack gap="lg">
      <Paper p="md" withBorder radius="md" shadow="sm">
        <Stack gap="md">
          {/* Header */}
          <div>
            <Title order={3} size="h4" mb={4}>
              <IconFileImport
                size={20}
                style={{ marginRight: 8, verticalAlign: 'middle' }}
              />
              Import danych godzinowych
            </Title>
            <Text size="sm" c="dimmed">
              Prześlij plik CSV z Tauron eLicznik, aby zaimportować dane o zużyciu energii
            </Text>
          </div>

          {/* Tariff Selector */}
          <TariffSelector value={tariff} onChange={setTariff} disabled={loading} />

          {/* Dropzone */}
          <Dropzone
            onDrop={handleDrop}
            onReject={handleReject}
            maxSize={20 * 1024 * 1024} // 20 MB
            accept={[MIME_TYPES.csv, 'text/csv', 'application/csv']}
            disabled={loading}
            multiple={false}
          >
            <Group
              justify="center"
              gap="xl"
              mih={120}
              style={{ pointerEvents: 'none' }}
            >
              <Dropzone.Accept>
                <IconUpload
                  style={{
                    width: rem(52),
                    height: rem(52),
                    color: 'var(--mantine-color-blue-6)',
                  }}
                  stroke={1.5}
                />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX
                  style={{
                    width: rem(52),
                    height: rem(52),
                    color: 'var(--mantine-color-red-6)',
                  }}
                  stroke={1.5}
                />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconFileTypeCsv
                  style={{
                    width: rem(52),
                    height: rem(52),
                    color: 'var(--mantine-color-dimmed)',
                  }}
                  stroke={1.5}
                />
              </Dropzone.Idle>

              <div>
                <Text size="lg" fw={500} inline>
                  Przeciągnij plik CSV tutaj lub kliknij, aby wybrać
                </Text>
                <Text size="sm" c="dimmed" inline mt={7}>
                  Plik nie może przekraczać 20 MB
                </Text>
              </div>
            </Group>
          </Dropzone>

          {/* Progress Indicator */}
          {loading && <ProgressIndicator progress={progress} />}

          {/* Error Display */}
          {error && (
            <Alert
              color="red"
              icon={<IconAlertCircle size={18} />}
              title="Błąd importu"
              withCloseButton
              onClose={reset}
            >
              <Text size="sm">{error}</Text>
            </Alert>
          )}

          {/* Upload Button - only show when NO preview (CsvPreview has its own import button) */}
          {!result && !preview && (
            <Group justify="flex-end">
              <Button
                onClick={handleUpload}
                disabled={!canUpload}
                loading={loading}
                leftSection={<IconUpload size={18} />}
                size="md"
              >
                {loading ? 'Importowanie...' : 'Importuj dane'}
              </Button>
            </Group>
          )}

          {/* Reset Button (after success) */}
          {result && !loading && (
            <Group justify="flex-end">
              <Button variant="light" onClick={handleReset} size="md">
                Importuj kolejny plik
              </Button>
            </Group>
          )}
        </Stack>
      </Paper>

      {/* CSV Preview (shown after file drop, before import) */}
      {preview && file && !result && (
        <CsvPreview
          preview={preview}
          fileName={file.name}
          onConfirm={handleUpload}
          onCancel={handleCancelPreview}
          loading={loading}
          tariffSelected={!!tariff}
        />
      )}

      {/* Import Summary */}
      {result && <ImportSummary result={result} />}
    </Stack>
  );
}
